import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';

// 设置密码弹窗（代理商/普通用户首次登录）
function SetPasswordModal({ onConfirm }) {
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const handleSubmit = async () => {
    if (!pwd || pwd.length < 6) {
      showToast('密码至少6位', 'error'); return;
    }
    if (pwd !== pwd2) {
      showToast('两次输入不一致', 'error'); return;
    }
    setLoading(true);
    try {
      await api.post('/auth/set-password', { password: pwd });
      showToast('密码设置成功', 'success');
      onConfirm();
    } catch (err) {
      showToast(err.response?.data?.error || '设置失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20
    }}>
      <div className="card" style={{ width: '100%', maxWidth: 340, padding: 28, borderRadius: 18 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: 'var(--c-text)' }}>设置登录密码</h2>
        <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 22, lineHeight: 1.5 }}>
          请设置您的固定登录密码，设置后无需再使用每日验证码，直接用手机号 + 密码登录。
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6, display: 'block' }}>新密码</label>
          <div style={{ position: 'relative' }}>
            <input
              className="input"
              type={show ? 'text' : 'password'}
              placeholder="请输入密码（至少6位）"
              value={pwd}
              onChange={e => setPwd(e.target.value)}
              style={{ paddingRight: 44 }}
            />
            <button type="button" onClick={() => setShow(!show)}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-tertiary)', padding: 4 }}>
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6, display: 'block' }}>确认密码</label>
          <input
            className="input"
            type="password"
            placeholder="再次输入密码"
            value={pwd2}
            onChange={e => setPwd2(e.target.value)}
          />
        </div>

        <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}
          style={{ height: 48, fontSize: 15, borderRadius: 12, fontWeight: 600 }}>
          {loading ? '保存中...' : '确认设置'}
        </button>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [credId, setCredId] = useState('');
  const [credential, setCredential] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needSetPassword, setNeedSetPassword] = useState(false);
  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credId.trim() || !credential.trim()) {
      showToast('请填写完整信息', 'error');
      return;
    }
    setLoading(true);
    try {
      const user = await login(credId, credential, credential);
      showToast('登录成功', 'success');
      // 代理商/普通用户首次登录（未设密码）→ 弹窗引导设置固定密码
      if ((user.role === 'agent' || user.role === 'user') && !user.has_password) {
        setNeedSetPassword(true);
        return;
      }
      navigate(user.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      showToast(err.response?.data?.error || '登录失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSet = () => {
    setNeedSetPassword(false);
    navigate('/');
  };

  return (
    <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh', padding: '20px', background: 'linear-gradient(180deg, #f5f5f7 0%, #e8e8ed 100%)' }}>
      {needSetPassword && <SetPasswordModal onConfirm={handlePasswordSet} />}

      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          {/* 网站 logo（和桌面图标 / 主屏幕图标同一张，public/logo512.png） */}
          <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="React" width={80} height={80}
            style={{ display: 'block', width: 80, height: 80, borderRadius: 20, margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(10,132,255,0.28)' }} />
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--c-text)', letterSpacing: '-0.02em' }}>React</h1>
          <p style={{ color: 'var(--c-text-secondary)', marginTop: 6, fontSize: 14, fontWeight: 500 }}>Keep it simple.</p>
        </div>

        <div className="card" style={{ padding: 28, borderRadius: 18, boxShadow: 'var(--shadow-lg)' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6, display: 'block' }}>
                账号
              </label>
              <input
                className="input"
                type="text"
                value={credId}
                onChange={e => setCredId(e.target.value)}
                autoComplete="username"
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6, display: 'block' }}>
                密码 / 验证码
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  value={credential}
                  onChange={e => setCredential(e.target.value)}
                  autoComplete="current-password"
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-tertiary)', padding: 4 }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button className="btn btn-primary" type="submit" disabled={loading}
              style={{ height: 50, fontSize: 16, borderRadius: 12, fontWeight: 600 }}>
              {loading ? '登录中...' : '登 录'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
